<template>
  <!-- 商品管理列表页（基础资料） -->
  <div class="product-list">
    <list-page>
      <search-condition>
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="名称/编码/拼音码" @keydown.enter="resetSearch">
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.searchType" placeholder="请选择商品类型" clearable>
            <template #prefix>商品类型</template>
            <el-option v-for="t in productTypeList" :key="t.code" :value="t.code" :label="t.name"/>
          </el-select>
          <el-select v-model="searchInfo.status" placeholder="请选择状态" clearable>
            <template #prefix>状态</template>
            <el-option label="上架" :value="1"/>
            <el-option label="下架" :value="0"/>
          </el-select>
          <el-select v-model="searchInfo.bankup1" placeholder="全部产品线" clearable filterable>
            <template #prefix>产品线</template>
            <el-option v-for="d in productLineList" :key="d.dictCode" :label="d.dictName" :value="d.dictCode"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="router.push('/product/info/detail')">新建</page-button>
          <page-button type="write" el-type="success" @click="router.push('/product/info/combo')" v-if="hasCombo()">新建组合商品</page-button>
          <page-button type="write" el-type="warning" :disabled="importStatus.running"
                       @click="showImport = true">{{ importStatus.running ? "导入中..." : "导入商品" }}</page-button>
          <page-button type="read" el-type="success" @click="exportList">导出</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <!-- 导入商品：编码前缀相同的连续行为下级 SKU -->
        <import-component ref="importComponent" v-model="showImport" title="导入商品"
                          :import-by-excel="handleImport" @export-temp="downloadTemplate"/>
        <separator-page>
          <template #category>
            <tree-dictionary v-model:dictionary-list="categoryTree" dictionary-type="PRODUCT_CATEGORY" @clickCategory="clickCategory" :filter="true" placeholder="输入商品分类名称过滤"/>
          </template>
          <template #default>
            <list-page>
              <list-table>
                <yq-table :data="dataList" height="100%" stripe border page-name="productInfo">
                  <yq-table-column label="图片" prop="image" width="60rem" align="center">
                    <template v-slot="{row}">
                      <image-show v-model="row.image" width="0.8rem" height="0.8rem"/>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="商品名称" prop="productName" min-width="180rem" :show-overflow-tooltip="true">
                    <template v-slot="{row}">
                      <a @click="toDetail(row)">{{row.productName}}</a>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="规格型号" prop="spec" min-width="160rem" :show-overflow-tooltip="true"/>
                  <yq-table-column label="商品编码" prop="productCodeBase" width="120rem" :show-overflow-tooltip="true"/>
                  <yq-table-column label="拼音码" prop="pinyinCode" width="120rem" :show-overflow-tooltip="true"/>
                  <yq-table-column label="商品类型" prop="productType" width="120rem" align="center">
                    <template v-slot="{row}">
                      <el-tag size="small" :type="typeTagType(row.productType)">{{typeName(row.productType)}}</el-tag>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="商品分类" prop="categoryId" width="180rem" :show-overflow-tooltip="true">
                    <template v-slot="{row}">
                      <dictionary :dictionaries="categoryTree" v-model="row.categoryId" :disabled="true"/>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="产品线" prop="bankup1" width="130rem" :show-overflow-tooltip="true">
                    <template v-slot="{row}">
                      {{ dictName(productLineList, row.bankup1) || '—' }}
                    </template>
                  </yq-table-column>
                  <yq-table-column label="毛利" prop="grossProfitRate" width="100rem" align="right">
                    <template v-slot="{row}">
                      <span v-if="grossProfitRate(row) !== null">{{ grossProfitRate(row) }}%</span>
                      <span v-else class="muted">—</span>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="累计销量" prop="totalSales" width="110rem" align="right">
                    <template v-slot="{row}">
                      <span v-if="row.totalSales !== null && row.totalSales !== undefined">{{ formatNumber(row.totalSales) }}</span>
                      <span v-else class="muted">—</span>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="商品单位" prop="unitIdBase" width="120rem" align="center">
                    <template v-slot="{row}">
                      <span v-for="u in unitList" :key="u.dictCode">
                        <el-tag v-if="u.dictCode === row.unitIdBase" type="success" size="small">{{u.dictName}}</el-tag>
                      </span>
                      <span v-for="u in unitList" :key="u.dictCode">
                        <el-tag v-if="u.dictCode === row.unitIdMiddle" type="primary" size="small">{{u.dictName}}</el-tag>
                      </span>
                      <span v-for="u in unitList" :key="u.dictCode">
                        <el-tag v-if="u.dictCode === row.unitIdLarge" type="warning" size="small">{{u.dictName}}</el-tag>
                      </span>
                    </template>
                  </yq-table-column>
                  <yq-table-column label="状态" prop="status" width="120rem" align="center">
                    <template v-slot="{row}">
                      <el-tag size="small" :type="row.status === 1 ? 'success' : 'info'">{{row.status === 1 ? '上架' : '下架'}}</el-tag>
                    </template>
                  </yq-table-column>
<!--                  <yq-table-column label="序列号" prop="serialEnabled" width="120rem" align="center">-->
<!--                    <template v-slot="{row}">-->
<!--                      <el-tag size="small" :type="row.serialEnabled === 1 ? 'warning' : 'info'">{{row.serialEnabled === 1 ? (row.serialRequired === 1 ? '严格' : '开启') : '关闭'}}</el-tag>-->
<!--                    </template>-->
<!--                  </yq-table-column>-->
                  <yq-table-column label="操作" prop="options" :align="'center'" fixed="right" width="120rem" :show-overflow-tooltip="false">
                    <template v-slot="{row}">
                      <table-button-group label="更多操作">
                        <table-button type="write" @click="toDetail(row)">编辑</table-button>
                        <table-button type="write" @click="updateStatus(row)">
                          {{row.status === 1 ? '下架' : '上架'}}
                        </table-button>
                        <table-button type="delete" el-type="danger" message="是否确认删除该商品" @click="toDelete(row)">删除</table-button>
                      </table-button-group>
                    </template>
                  </yq-table-column>
                </yq-table>
              </list-table>
              <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                         :total-count="searchInfo.totalCount" @onLoad="queryList"/>
            </list-page>
          </template>
        </separator-page>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import SeparatorPage from "@/components/h5/layout/SeparatorPage.vue";
import TableButtonGroup from "@/components/h5/button/TableButtonGroup.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import Dictionary from "@/components/h5/data/Dictionary.vue";
import ImageShow from "@/components/h5/image/ImageShow.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import productApi from "@/api/normal/product/productApi.js";
import TreeDictionary from "@/components/h5/data/TreeDictionary.vue";
import commonApi from "@/api/commonApi";
import util from "@/util/util";
import excelUtil from "@/util/excelUtil";
import ImportComponent from "@/components/h5/ImportComponent.vue";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ProductList",
  components: {
    ImportComponent,
    ImageShow, Dictionary, TreeDictionary, YqTableColumn, TableButtonGroup, TableButton, SearchCondition, FormPage, ListTable, ListPage, SeparatorPage
  },
  data() {
    return {
      searchInfo: {pageNumber: 1},
      dataList: [],
      categoryTree: [],
      // 商品类型 1 库存 2虚拟商品 3计次商品 4组合商品
      productTypeList: [],
      unitList: [], // 计量单位
      productLineList: [], // 产品线（普通字典 CRM_PRODUCT_LINE）
      showImport: false,
      // 导入状态：后端异步导入，前端每5秒轮询一次
      importStatus: {running: false, status: "NONE"},
      importPollTimer: null,
      // 只有当前页面发起的导入（或进入页面时正在导入）才在完成时提示结果
      importPollingNotified: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    this.stopImportPolling();
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  unmounted() {
    this.stopImportPolling();
  },
  created() {
    this.resetSearch();
    this.initImportStatus();
    // 计量单位
    commonApi.queryUniqueDictionaryList("PRODUCT_UNIT").then(list => this.unitList = list);
    // 产品线：普通字典（值存 ba_product.bankup1）
    commonApi.queryDictionaryList("CRM_PRODUCT_LINE").then(list => this.productLineList = list || []);
    // 获取系统配置 商品类型配置
    util.getSystemSettingOptions("PRODUCT_STOCK_TYPE").then(list => this.productTypeList = list);
  },
  methods: {
    // 是否有组合商品
    hasCombo() {
      for (let i = 0; i < this.productTypeList.length; i++) {
        if (this.productTypeList[i].code === 4) {
          return true;
        }
      }
      return false;
    },
    // 查询列表
    queryList() {
      productApi.queryList(this.searchInfo).then(res => {
        this.dataList = res.records || [];
        this.searchInfo.totalCount = res.totalCount || res.total || 0;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    /** 下载导入模板 */
    downloadTemplate() {
      excelUtil.exportExcel("/basedata/product/info/importTemplate", {}, "商品数据导入模板.xlsx");
    },
    /** 上传回调：后台异步导入，接口立即返回，前端轮询导入状态 */
    handleImport(formData) {
      return productApi.importExcel(formData).then(() => {
        this.$message.success("商品正在导入中，请稍后查询");
        this.showImport = false;
        this.importStatus = {running: true, status: "RUNNING"};
        this.importPollingNotified = true;
        // 清掉通用组件里选中的文件，下次打开不要还挂着上一次的文件
        if (this.$refs.importComponent) {
          this.$refs.importComponent.fileList = [];
          this.$refs.importComponent.file = "";
        }
        this.startImportPolling();
      }).catch(() => {
        // 上一次导入未完成等错误由请求拦截器提示，这里同步一下真实状态
        this.initImportStatus();
      });
    },
    /** 进入页面先查一次：如果后台正在导入，继续轮询 */
    initImportStatus() {
      productApi.importStatus().then(status => {
        this.importStatus = status || {running: false, status: "NONE"};
        if (this.importStatus.running) {
          this.importPollingNotified = true;
          this.startImportPolling();
        }
      });
    },
    startImportPolling() {
      if (this.importPollTimer) {
        return;
      }
      this.importPollTimer = setInterval(this.pollImportStatus, 5000);
    },
    stopImportPolling() {
      if (this.importPollTimer) {
        clearInterval(this.importPollTimer);
        this.importPollTimer = null;
      }
    },
    pollImportStatus() {
      productApi.importStatus().then(status => {
        this.importStatus = status || {running: false, status: "NONE"};
        if (this.importStatus.running) {
          return;
        }
        this.stopImportPolling();
        if (!this.importPollingNotified) {
          return;
        }
        this.importPollingNotified = false;
        if (status.status === "SUCCESS") {
          this.$message.success(status.message || "商品导入完成");
          this.resetSearch();
        } else if (status.status === "FAILED") {
          this.$message.error("商品导入失败：" + (status.message || "请查看后台日志"));
        }
      }).catch(() => {
        // 轮询失败不停止，下一次继续
      });
    },
    /** 导出当前查询条件下的商品及 SKU */
    exportList() {
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined || k === "totalCount") {
          return;
        }
        params[k] = v;
      });
      params.pageNumber = 1;
      excelUtil.exportExcel("/basedata/product/info/exportList", params, "商品数据.xlsx");
    },
    // 点击分类树节点过滤
    clickCategory(node) {
      this.searchInfo.category = node.dictCode || '';
      this.resetSearch();
    },
    // 跳转详情/编辑
    toDetail(row) {
      if (row.productType === 4) { // 组合商品
        this.router.push(`/product/info/combo?id=${row.id}`);
      } else {
        this.router.push(`/product/info/detail?id=${row.id}`);
      }

    },
    // 上下架
    updateStatus(row) {
      let newStatus = row.status === 1 ? 0 : 1;
      productApi.updateStatus(row.id, newStatus).then(() => {
        row.status = newStatus;
        this.$message.success(newStatus === 1 ? "已上架" : "已下架");
      });
    },
    // 删除
    toDelete(row) {
      productApi.delete(row.id).then(() => {
        this.queryList();
      })
    },
    // 商品类型名称
    typeName(type) {
      let t = this.productTypeList.find(x => x.code === type);
      return t ? t.name : type;
    },
    // 字典值转名称（普通字典）
    dictName(list, value) {
      if (!value) return '';
      const d = (list || []).find(x => x.dictCode === value);
      return d ? d.dictName : '';
    },
    // 毛利(纯前端展示) = (销售价格 - 成本价格) / 销售价格，保留 2 位；销售价为空/0 时返回 null
    grossProfitRate(row) {
      const sale = Number(row.salePriceBase);
      const buy = Number(row.buyPriceBase);
      if (!sale || isNaN(sale) || isNaN(buy)) return null;
      return Math.round(((sale - buy) / sale) * 10000) / 100;
    },
    formatNumber(value) {
      const n = Number(value);
      return isNaN(n) ? '—' : n.toLocaleString('zh-CN', {maximumFractionDigits: 2});
    },
    // 商品类型标签颜色
    typeTagType(type) {
      switch (type) {
        case 1: return 'success';   // 库存
        case 2: return 'info';      // 服务
        case 3: return 'warning';   // 计次
        case 4: return 'danger';    // 组合
        default: return 'info';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.importing-tip {
  margin-left: 10px;
  font-size: 12px;
  color: var(--el-color-warning, #e6a23c);
}

.product-list {
  height: 100%;
  .category-tree {
    height: 100%;
    padding: 10px;
    box-sizing: border-box;
    :deep(.el-tree) {
      overflow: auto;
      .el-tree-node__content {
        height: 32px;
      }
    }
  }
}
</style>
